import rybbit from "@rybbit/js";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { StrictMode, Suspense, useEffect } from "react";
import {
	type MutableSnapshot,
	RecoilEnv,
	RecoilRoot,
	useSetRecoilState,
} from "recoil";
import { InjectedStackCfKeyAtom } from "./state/stack";
import {
	ContainerSelector,
	HostId,
	IsEmbedded,
	IsInAuthor,
	IsInPopOut,
	IsPopOutInDev,
	IsPreviewGenerator,
} from "./state/ui";

gsap.registerPlugin(ScrollTrigger);

import { type Message, NormalizeCss, strings } from "@quickpoint.me/shared";
import Display from "./Display";
import RefreshSpinner from "./displayComponents/Refresh";
import DisplayStickyFooter from "./displayComponents/displayElements/DisplayStickyFooter";
import DisplayStickyHeader from "./displayComponents/displayElements/DisplayStickyHeader";
import FindMoveBy from "./functionalComponents/FindMoveBy";
import FindStickyFooter from "./functionalComponents/FindStickyFooter";
import FindStickyHeader from "./functionalComponents/FindStickyHeader";
import PageSize from "./functionalComponents/FrameSize";
import MessageHandler from "./functionalComponents/MessageHandler";
import PreRender from "./functionalComponents/PreRender";
import Preloader from "./functionalComponents/Preloader";
import PreviewRequester from "./functionalComponents/PreviewRequester";
import { QpHistory } from "./functionalComponents/QpHistory";
import RenderPage from "./functionalComponents/RenderPage";
import RequestDataFromAuthor from "./functionalComponents/RequestDataFromAuthor";

import ReadingRulerOverlay from "./displayComponents/displayElements/ReadingRulerOverlay";
import { AnalyticsTracker } from "./functionalComponents/AnalyticsTracker";
import HeatmapTracker from "./functionalComponents/HeatmapTracker";
import SyncManager from "./functionalComponents/SyncManager";
import { TriggerAutoPlay } from "./functionalComponents/TriggerAutoPlay";

RecoilEnv.RECOIL_GKS_ENABLED.delete("recoil_memory_managament_2020");

const config = {
	analyticsHost: "https://app.rybbit.io/api",
	siteId: "1161",
};

// Disabled pending a decision on whether to keep this bespoke pipeline or
// fold heatmap/session-replay into Rybbit instead — see HeatmapTracker.tsx.
const HEATMAP_TRACKER_ENABLED = false;

const ConditionalHeatmapTracker = ({
	isInAuthor,
	isEmbedded,
}: {
	isInAuthor: boolean;
	isEmbedded: boolean;
}) => {
	if (!HEATMAP_TRACKER_ENABLED) return null;
	if (import.meta.env.DEV) return null;
	if (!isInAuthor && !isEmbedded) {
		return <HeatmapTracker />;
	}

	return null;
};

function SyncIsInAuthor({ isInAuthor }: { isInAuthor: boolean }) {
	const setIsInAuthor = useSetRecoilState(IsInAuthor);
	useEffect(() => {
		setIsInAuthor(isInAuthor);
	}, [isInAuthor, setIsInAuthor]);
	return null;
}

const Viewerati = ({
	isEmbedded = false,
	containerSelector,
	hostId,
	isInAuthor = false,
	isInPopOut = false,
	isPreviewer = false,
	onReady,
	stackCfKey,
}: {
	isEmbedded?: boolean;
	containerSelector: string;
	hostId: string;
	isInAuthor?: boolean;
	isInPopOut?: boolean;
	isPreviewer?: boolean;
	onReady?: () => void;
	stackCfKey?: string;
}) => {
	if (!isInAuthor && !isEmbedded && !isPreviewer) {
		rybbit.init(config);
		rybbit.pageview();
	}

	const popDev = window.location.host === "localhost:3000";

	const initializeState = ({ set }: { set: MutableSnapshot["set"] }) => {
		if (stackCfKey) {
			set(InjectedStackCfKeyAtom, stackCfKey);
		}
		const allowEmbeddedDataFetchURL = !!new URLSearchParams(
			document.location.search,
		).get("isEmbedded");

		const isEmbeddedURL = !!new URLSearchParams(document.location.search).get(
			"isInAuthor",
		);
		const isInPopOutURL = !!new URLSearchParams(document.location.search).get(
			"pop",
		);
		const popHostId = new URLSearchParams(document.location.search).get(
			"hostId",
		);

		set(ContainerSelector, containerSelector);

		if (hostId) {
			set(HostId, popHostId ? popHostId : hostId);
		} else {
			throw Error("HostId must be defined");
		}

		if (popDev) {
			set(IsPopOutInDev, popDev);
			// @ts-expect-error: fix this
			window.IsPopOutInDev = true;
		}

		if (isEmbedded) {
			set(
				IsEmbedded,
				allowEmbeddedDataFetchURL ? allowEmbeddedDataFetchURL : isEmbedded,
			);
		}

		if (isInAuthor) {
			set(IsInAuthor, isEmbeddedURL ? isEmbeddedURL : isInAuthor);
		}

		if (isInPopOut) {
			set(IsInPopOut, isInPopOutURL ? isInPopOutURL : isInPopOut);
		}

		if (isPreviewer) {
			set(IsPreviewGenerator, isPreviewer);
		}
	};

	useEffect(() => {
		if (isInAuthor || isPreviewer) {
			ScrollTrigger.disable(true);
		} else {
			ScrollTrigger.enable();
		}
	}, [isInAuthor, isPreviewer]);

	useEffect(() => {
		if (onReady) {
			onReady();
			return;
		}
		const bc = new BroadcastChannel(strings.broadcastChannel.author);
		const message: Message = {
			hostId,
			type: "preview",
			action: strings.messages.ready,
		};
		bc.postMessage(message);
		return () => {
			bc.close();
		};
	}, [hostId, onReady]);

	if (!isInAuthor && !isEmbedded && !isPreviewer) {
		console.info("Viewer Build Date: ", import.meta.env.VITE_BUILD_DATE);
	}

	return (
		<RecoilRoot initializeState={initializeState}>
			<SyncIsInAuthor isInAuthor={isInAuthor} />
			<Suspense fallback={<RefreshSpinner />}>
				<StrictMode>
					<NormalizeCss />
					<AnalyticsTracker />
					<DisplayStickyHeader />
					<DisplayStickyFooter />
					{isPreviewer && <PreviewRequester />}
					<MessageHandler />
					<RequestDataFromAuthor />
					<TriggerAutoPlay />
					<Preloader />
					<FindStickyHeader />
					<FindStickyFooter />
					<FindMoveBy />
					<Display container={null} />
					<PageSize containerSelector={containerSelector} />
					<RenderPage />
					<ReadingRulerOverlay />
					{!isInAuthor && <PreRender />}
					<ConditionalHeatmapTracker
						isInAuthor={isInAuthor}
						isEmbedded={isEmbedded}
					/>
					{isEmbedded && <SyncManager />}
				</StrictMode>
				<QpHistory />
			</Suspense>
		</RecoilRoot>
	);
};

export default Viewerati;
